import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../api/task/taskApi';
import { useTaskPartyNames } from '../hooks/useTaskPartyNames';
import {
  HOME_VISIBLE_TASKS,
  defaultTaskSortBy,
} from '../pages/v3/Tasks/taskTabs';
import { WorkspaceTask } from '../types/taskTypes';
import { toTitleCase } from '../utils/helpers/common';
import { EntityList } from './EntityList';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';
import { SnapshotListSkeleton } from './SnapshotListSkeleton';
import { TaskCard, TaskCardSkeleton, buildTaskCardViewUrl } from './TaskCard';
import { TaskSnapshotViewAll } from './TaskSnapshotViewAll';

export type PotentialClaimTask = WorkspaceTask;

const TASK_TYPE = TaskTypeEnum.PotentialClaim;

const PotentialClaimsSkeleton = () => (
  <SnapshotListSkeleton
    label="Loading potential claim tasks"
    hasTitle
    numCards={HOME_VISIBLE_TASKS}
    renderCard={() => <TaskCardSkeleton type={TASK_TYPE} />}
  />
);

const PotentialClaimContent = ({
  tasks,
  totalTasks,
}: {
  tasks: PotentialClaimTask[];
  totalTasks: number;
}) => {
  const { search } = useLocation();
  const { workspaceId } = useParams();

  // The section paints once the tasks and the workspace type are in; only the
  // client name, which needs a further request, carries a placeholder.
  const { getPartyNames, isWorkspaceTypePending } = useTaskPartyNames(
    tasks,
    workspaceId,
  );

  if (isWorkspaceTypePending) {
    return <PotentialClaimsSkeleton />;
  }

  return (
    <EntityList>
      <EntityList.Header>
        <EntityList.Title>{toTitleCase(TASK_TYPE)} Tasks</EntityList.Title>
        <EntityList.Count count={totalTasks} />

        <TaskSnapshotViewAll
          segment="potential"
          totalTasks={totalTasks}
          workspaceId={workspaceId}
        />
      </EntityList.Header>

      {tasks.length === 0 ? (
        <EntityList.Empty
          title="There are no potential claims"
          description="If we find a potential claim you may be able to register, you’ll see it displayed here and on the tasks page."
        />
      ) : (
        <EntityList.Content>
          {tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              type={TASK_TYPE}
              {...getPartyNames(task)}
              viewUrl={buildTaskCardViewUrl(task, TASK_TYPE, search)}
            />
          ))}
        </EntityList.Content>
      )}
    </EntityList>
  );
};

export const PotentialClaimsSnapshot = () => {
  const { workspaceId } = useParams();

  const tasksQuery = useQuery(
    queryOptionsGetTasksPaginated({
      pageSize: HOME_VISIBLE_TASKS,
      sortBy: defaultTaskSortBy(TaskTypeEnum.PotentialClaim),
      type: TaskTypeEnum.PotentialClaim,
      workspaceId,
    }),
  );

  return (
    <SingleQueryResults
      loadingFallback={<PotentialClaimsSkeleton />}
      query={tasksQuery}
    >
      {(tasksResponse) => {
        const tasks = (tasksResponse?.items as PotentialClaimTask[]) || [];

        return (
          <PotentialClaimContent
            tasks={tasks}
            totalTasks={tasksResponse?.meta?.totalItems || 0}
          />
        );
      }}
    </SingleQueryResults>
  );
};
